import { Injectable } from "@angular/core";
import { DateTime, Settings } from "luxon";
import { User } from "src/app/core/models/user.model";
import { environment } from "src/environments/environment";
import { GraphGrouping, Timeframe } from "../types/hydrao-types";

export class ValueWithUnit {
  constructor(public value: number, public unit: string) {}

  public toString() {
    return `${this.value ? this.value : 0} ${this.unit}`;
  }
}

@Injectable({
  providedIn: "root",
})
export class UtilsService {
  public gallonsToLiters(gallons: number) {
    return Math.round(3.78541 * gallons);
  }

  public literToGallons(liters: number) {
    return Math.round(liters * 0.264172 * 10) / 10;
  }

  public literToCCF(liters: number) {
    return Math.round(liters * 0.000353147 * 10) / 10;
  }

  public CCFToLiter(liters: number) {
    return Math.round(liters * 2831.7);
  }

  public celciusToFahrenheit(degrees: number) {
    return Math.round(degrees * 1.8 + 32);
  }

  public get locale() {
    let locale = "en";

    if (environment.production && window.location.pathname.startsWith("/fr")) {
      locale = "fr";
    }
    return locale;
  }

  public get StatsValue(): number {
    let statsValue = Number(localStorage.getItem("stats"));
    //default : last month
    return statsValue && !isNaN(statsValue) ? statsValue : 43800;
  }

  public set StatsValue(value: number) {
    localStorage.setItem("stats", value.toString());
  }

  public toBase64Url(inputString) {
    return btoa(inputString)
      .replace(/=/g, "")
      .replace(/\+/g, "-")
      .replace(/\//g, "_");
  }

  public getVolumeFormattedWithUser(user: User, liters: number): ValueWithUnit {
    if (user && user.useLiters()) {
      if (liters > 999) {
        return new ValueWithUnit(Number((liters / 1000).toFixed(1)), "m³");
      } else if (liters >= 100) {
        return new ValueWithUnit(Math.round(liters), "L");
      } else {
        return new ValueWithUnit(liters, "L");
      }
    } else {
      if (this.literToGallons(liters) > 999) {
        return new ValueWithUnit(this.literToCCF(liters), "CCF");
      } else {
        return new ValueWithUnit(this.literToGallons(liters), "gal");
      }
    }
  }

  public getTemperatureFormattedWithUser(
    user: User,
    temperature: number
  ): ValueWithUnit {
    if (user && user.useLiters()) {
      return new ValueWithUnit(temperature, "°C");
    } else {
      return new ValueWithUnit(this.celciusToFahrenheit(temperature), "°F");
    }
  }

  public getFlowFormattedWithUser(user: User, flow: number): ValueWithUnit {
    if (user && user.useLiters()) {
      return new ValueWithUnit(flow, "L/min");
    } else {
      return new ValueWithUnit(this.literToGallons(flow), "gal/min");
    }
  }

  public getHourlyFlowFormattedWithUser(
    user: User,
    flow: number
  ): ValueWithUnit {
    if (user && user.useLiters()) {
      return new ValueWithUnit(flow, "L/h");
    } else {
      return new ValueWithUnit(this.literToGallons(flow), "gal/h");
    }
  }

  public calculateElapsedSeconds(dateTime: Date): number {
    const now = new Date();
    // console.log("calculateElapsedSeconds", dateTime);
    let timeDiffMilliseconds = now.getTime();
    if (dateTime) {
      timeDiffMilliseconds = now.getTime() - dateTime.getTime(); // diff in milliseconds
    }
    return Math.floor(timeDiffMilliseconds / 1000); // convert to seconds and round to upper integer
  }

  public getDurationFormatted(
    seconds: number,
    maxUnits: number = 2,
    short: boolean = true,
    moreThan: boolean = true
  ): string {
    const units = [
      { name: "year", seconds: 365 * 24 * 60 * 60 },
      { name: "month", seconds: 30 * 24 * 60 * 60 },
      { name: "day", seconds: 24 * 60 * 60 },
      { name: "hour", seconds: 60 * 60 },
      { name: "minute", seconds: 60 },
      { name: "second", seconds: 1 },
    ];

    let remaining = Math.abs(seconds);
    const parts = [];
    for (const unit of units) {
      const value = Math.floor(remaining / unit.seconds);
      if (value > 0) {
        let part = null;
        switch (unit.name) {
          case "year":
            if (short === true)
              part = $localize`:@@global.duration.shortYear:${value}:year:y`;
            else {
              part =
                value === 1
                  ? $localize`:@@global.duration.oneYear:a year`
                  : $localize`:@@global.duration.manyYears:${value}:year: years`;
            }
            break;
          case "month":
            if (short === true)
              part = $localize`:@@global.duration.shortMonth:${value}:month:m`;
            else {
              part =
                value === 1
                  ? $localize`:@@global.duration.oneMonth:a month`
                  : $localize`:@@global.duration.manyMonths:${value}:month: months`;
            }
            break;
          case "day":
            if (short === true)
              part = $localize`:@@global.duration.shortDay:${value}:day:d`;
            else {
              part =
                value === 1
                  ? $localize`:@@global.duration.oneDay:a day`
                  : $localize`:@@global.duration.manyDays:${value}:day: days`;
            }
            break;
          case "hour":
            if (short === true)
              part = $localize`:@@global.duration.shortHour:${value}:hour:h`;
            else {
              part =
                value === 1
                  ? $localize`:@@global.duration.oneHour:an hour`
                  : $localize`:@@global.duration.manyHours:${value}:hour: hours`;
            }
            break;
          case "minute":
            if (short === true)
              part = $localize`:@@global.duration.shortMinute:${value}:minute:min`;
            else {
              part =
                value === 1
                  ? $localize`:@@global.duration.oneMinute:a minute`
                  : $localize`:@@global.duration.manyMinutes:${value}:minute: minutes`;
            }
            break;
          case "second":
            if (short === true)
              part = $localize`:@@global.duration.shortSecond:${value}:second:s`;
            else {
              part =
                value === 1
                  ? $localize`:@@global.duration.oneSecond:a second`
                  : $localize`:@@global.duration.manySeconds:${value}:second: seconds`;
              break;
            }
        }
        parts.push(part);
        remaining -= value * unit.seconds;
      }
      if (parts.length === maxUnits) break; // Stop at 2 components
    }

    // console.log("UTILS / getFormattedDuration PARTS", parts);

    if (parts.length === 0) {
      return $localize`:@@global.duration.now:now`;
    } else if (parts.length === 1) {
      if (remaining > 0 && moreThan) {
        // case = more than ...
        return (
          $localize`:@@global.duration.moreThan:more than` + " " + parts[0]
        );
      } else {
        return parts[0];
      }
    } else if (parts.length === 2) {
      if (short === true) {
        return parts[0] + " " + parts[1];
      } else {
        return (
          parts[0] +
          " " +
          $localize`:@@global.duration.and:and` +
          " " +
          parts[1]
        );
      }
    } else if (parts.length > 2) {
      if (short === true) return parts.join(" ");
      else {
        const lastPart = parts.pop();
        return (
          parts.join(" ") +
          " " +
          $localize`:@@global.duration.and:and` +
          " " +
          lastPart
        );
      }
    }
  }

  public formatSince(
    date: Date,
    thresholdInDays: number = 7,
    maxUnits: number = 1,
    short: boolean = false,
    locale: string = $localize.locale || "en"
  ): string {
    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffDays = diffSeconds / 86400;

    let sinceLabel;
    if (diffSeconds === 0) {
      return $localize`:@@global.duration.now:now`;
    }
    if (diffDays < thresholdInDays) {
      sinceLabel = $localize`:@@global.date.sinceRelative:since`;

      const duration = this.getDurationFormatted(diffSeconds, maxUnits, short);

      return sinceLabel + " " + duration;
    }

    sinceLabel = $localize`:@@global.date.sinceDate:since`;
    const formatter = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    return sinceLabel + " " + formatter.format(date);
  }

  public formatRelative(
    date: Date,
    thresholdInDays: number = 7,
    maxUnits: number = 1,
    short: boolean = false,
    locale: string = $localize.locale || "en"
  ): string {
    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffDays = diffSeconds / 86400;

    let sinceLabel;
    if (diffSeconds === 0) {
      return $localize`:@@global.duration.now:now`;
    }
    if (diffDays < thresholdInDays) {
      const duration = this.getDurationFormatted(diffSeconds, maxUnits, short);

      return $localize`:@@global.date.relativeAgo:${duration}:relativeDate: ago`;
    }

    sinceLabel = $localize`:@@global.duration.relativeOn:on`;
    const formatter = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    try {
      return sinceLabel + " " + formatter.format(date);
    } catch (e) {
      console.error("FAILED to format relative date", date);
      return "";
    }
  }

  public formatDatetime(date: Date, locale = $localize.locale || "en"): string {
    const formatter = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    try {
      return formatter.format(date);
    } catch (e) {
      console.error("FAILED to format relative date", date);
      return "";
    }
  }

  public getTimezone(): string {
    return Settings.defaultZone
      ? Settings.defaultZone.name
      : Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public getStartDateForTimeFrame(date: DateTime, tf: Timeframe): DateTime {
    return date.toLocal().startOf(tf);
  }

  public getEndDateForTimeFrame(date: DateTime, tf: Timeframe): DateTime {
    return date.toLocal().endOf(tf);
  }

  public getGraphEndDate(date: DateTime, graphGrouping: GraphGrouping) {
    let duration;
    let plus;
    switch (graphGrouping) {
      case GraphGrouping.days:
        duration = "day";
        plus = { days: 1 };
        break;
      case GraphGrouping.hours:
        duration = "hour";
        plus = { hours: 1 };
        break;
      case GraphGrouping.months:
        duration = "month";
        plus = { months: 1 };
        break;
      default:
        return null;
    }

    return date.plus(plus).startOf(duration);
  }

  public getGraphGroupingFromTimeFrame(tf: Timeframe) {
    switch (tf) {
      case Timeframe.Hour:
        return GraphGrouping.raw;
      case Timeframe.Day:
        return GraphGrouping.hours;
      case Timeframe.Week:
      case Timeframe.Month:
        return GraphGrouping.days;
      case Timeframe.Year:
        return GraphGrouping.months;
    }
  }

  public getD3TimeLocale(): d3.TimeLocaleDefinition {
    //returned from https://unpkg.com/d3-time-format@2.3.0/locale/en-US.json
    if (this.locale == "fr") {
      return {
        dateTime: "%A %e %B %Y à %X",
        date: "%d/%m/%Y",
        time: "%H:%M:%S",
        periods: ["AM", "PM"],
        days: [
          "dimanche",
          "lundi",
          "mardi",
          "mercredi",
          "jeudi",
          "vendredi",
          "samedi",
        ],
        shortDays: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
        months: [
          "janvier",
          "février",
          "mars",
          "avril",
          "mai",
          "juin",
          "juillet",
          "août",
          "septembre",
          "octobre",
          "novembre",
          "décembre",
        ],
        shortMonths: [
          "janv.",
          "févr.",
          "mars",
          "avr.",
          "mai",
          "juin",
          "juil.",
          "août",
          "sept.",
          "oct.",
          "nov.",
          "déc.",
        ],
      };
    } else {
      return {
        dateTime: "%x, %X",
        date: "%-m/%-d/%Y",
        time: "%-I:%M:%S %p",
        periods: ["AM", "PM"],
        days: [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        shortDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        months: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],
        shortMonths: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
      };
    }
  }
}
